import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { usePWA } from '../contexts/PWAContext';
import { BRAND_NAME } from '../constants/brand';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function PWAInstallPrompt() {
  const { isPromptVisible, dismissInstallPrompt, hideInstallPrompt, isFabPresent } = usePWA();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [hasPromptEvent, setHasPromptEvent] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini infobar from appearing on mobile
      e.preventDefault();
      setDeferredPrompt(e);
      setHasPromptEvent(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    hideInstallPrompt();
    deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      // User installed — permanently dismiss
      dismissInstallPrompt();
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    dismissInstallPrompt();
  };

  // Only show if context says to show AND we have a beforeinstallprompt event (Android/Chrome).
  // Suppress while a FAB is on screen (#3096) — the FAB is a core Action-layer
  // control and this z-50 card would otherwise cover it.
  if (!isPromptVisible || !hasPromptEvent || isFabPresent) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-oro-bg border border-accent/20 rounded-lg p-4 shadow-xl backdrop-blur-sm">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center font-bold text-black text-sm">
              O
            </div>
            <h3 className="text-text-primary font-semibold text-sm">Install {BRAND_NAME}</h3>
          </div>
          <button
            onClick={handleDismiss}
            className="text-text-muted hover:text-text-primary transition-colors p-1"
            type="button"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-text-secondary text-xs mb-3">
          Get the full app experience with faster loading and offline access.
        </p>

        <button
          onClick={handleInstallClick}
          type="button"
          className="w-full bg-accent hover:bg-accent/90 text-black font-medium py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
        >
          <Download size={16} />
          <span>Install App</span>
        </button>
      </div>
    </div>
  );
}
